import React from "react";
import styled from "styled-components";
import { Box } from "@bca/ui-library";

import {
  IEventPushable,
  IVehicleDetailsModule,
} from "../common/analytics/analytics.types";
import ReportItem from "./mechanical-report/ReportItem";
import SlashWrenchSVG from "../icons/MechanicalReportIcons/SlashWrenchSVG";
import WrenchSVG from "../icons/MechanicalReportIcons/WrenchSVG";
import {
  CategoryMapping,
  ReportTypes,
} from "../vehicle-search-details/vehicleSearch.type";
import ReportTitle from "./mechanical-report/ReportTitle";
import { ViewportSizeType } from "../hooks/useViewport.hook";
import KeyDecisionInfoContainer from "./KeyDecisionInfoContainer";
import { MechanicalReportAnalytics } from "../analytics/MechanicalReportAnalytics";
import analytics from "../common/analytics/analytics";

interface MechanicalReportProps {
  reportType?: ReportTypes;
  anchorLink?: string;
  vehicleAnalytics?: IVehicleDetailsModule;
  categoryMapping: CategoryMapping[] | undefined;
  viewport?: ViewportSizeType;
}

const StyledReportSummary = styled(Box)<{
  viewport: ViewportSizeType;
}>`
  justify-content: ${(props) =>
    props.viewport === "mobile" ? "space-between" : "initial"};
  display: flex;
  width: 100%;
  gap: ${(props) => (props.viewport === "mobile" ? "0px" : "4px")};
`;

const reportTitle = {
  EssentialChecks: "BCA Essential Check",
  Standard: "BCA Assured",
  ElectricHybrid: "BCA Assured EV/Hybrid",
  FullInspection: "128 Vehicle Inspection",
  NoReport: "No Report",
};

const MechanicalReport: React.FunctionComponent<MechanicalReportProps> = ({
  reportType = "NoReport",
  anchorLink,
  viewport,
  vehicleAnalytics,
  categoryMapping,
}) => {
  const handleMechanicalReport = (event) => {
    if (!anchorLink) {
      return;
    }
    event.stopPropagation();

    const result = MechanicalReportAnalytics({
      reportType,
      categoryMapping,
    });

    analytics.push<IEventPushable>({
      event: {
        name: "click",
        category: "link",
        label: result?.toString(),
        action: "navigate",
        section: "card",
      },
      product: {
        vehicle: [vehicleAnalytics],
      },
    });

    window.open(anchorLink, "_blank");
  };

  const title = reportTitle[reportType];

  const categoryKeys = [
    "Engine",
    "Gearbox",
    "Fluids",
    "WarningLights",
    "Other",
    "Wheel&Tyres",
  ];

  const categories = categoryMapping ? [...categoryMapping] : null;

  const sortedCategoryMapping = categories?.sort(
    (a, b) => categoryKeys.indexOf(a.key) - categoryKeys.indexOf(b.key)
  );

  const MechanicalReport = (reportType: ReportTypes) => {
    switch (reportType) {
      case "NoReport":
        return (
          <>
            {viewport === "mobile" ? (
              <KeyDecisionInfoContainer
                anchorLink={anchorLink}
                onClick={handleMechanicalReport}
                viewport={viewport}
              >
                <ReportTitle title={title}>{<SlashWrenchSVG />}</ReportTitle>
              </KeyDecisionInfoContainer>
            ) : (
              <KeyDecisionInfoContainer
                anchorLink={anchorLink}
                onClick={handleMechanicalReport}
                viewport={viewport}
              >
                <ReportTitle title={title} />
                <SlashWrenchSVG />
              </KeyDecisionInfoContainer>
            )}
          </>
        );
      case "FullInspection":
        return (
          <>
            {viewport === "mobile" ? (
              <KeyDecisionInfoContainer
                anchorLink={anchorLink}
                onClick={handleMechanicalReport}
                viewport={viewport}
              >
                <ReportTitle title={title}>
                  <WrenchSVG />
                </ReportTitle>
              </KeyDecisionInfoContainer>
            ) : (
              <KeyDecisionInfoContainer
                anchorLink={anchorLink}
                onClick={handleMechanicalReport}
                viewport={viewport}
              >
                <ReportTitle title={title} />
                <WrenchSVG />
              </KeyDecisionInfoContainer>
            )}
          </>
        );
      default:
        return (
          <>
            <>
              {sortedCategoryMapping?.length > 0 ? (
                <KeyDecisionInfoContainer
                  anchorLink={anchorLink}
                  onClick={handleMechanicalReport}
                  viewport={viewport}
                >
                  <ReportTitle title={title} />
                  <StyledReportSummary viewport={viewport}>
                    {categoryMapping.map((category) => {
                      return (
                        <ReportItem
                          key={category.key}
                          categoryKey={category.key}
                          severityCode={category.severityCode}
                          numberOfAmber={category.numberOfAmber}
                          numberOfRed={category.numberOfRed}
                        />
                      );
                    })}
                  </StyledReportSummary>
                </KeyDecisionInfoContainer>
              ) : null}
            </>
          </>
        );
    }
  };

  return <>{MechanicalReport(reportType)}</>;
};

export default MechanicalReport;
